import React, { useRef } from 'react';
import styled from 'styled-components';
import { motion, useInView } from 'framer-motion';

const ExperienceContainer = styled.section`
  padding: var(--spacing-xl) 0;
  background-color: var(--color-bg-dark);
  position: relative;
  overflow: hidden;
`;

const GradientBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at 30% 70%, var(--color-accent-secondary) 0%, transparent 70%);
  opacity: 0.1;
  z-index: 0;
  pointer-events: none;
`;

const ContentContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 var(--spacing-md);
  position: relative;
  z-index: 1;
`;

const SectionHeading = styled.div`
  text-align: center;
  margin-bottom: var(--spacing-lg);
`;

const HeadingLabel = styled(motion.div)`
  color: var(--color-accent-secondary);
  text-transform: uppercase;
  letter-spacing: 3px;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: var(--spacing-sm);
`;

const SectionTitle = styled(motion.h2)`
  margin-bottom: var(--spacing-md);
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background: linear-gradient(
      90deg,
      var(--color-accent-secondary) 0%,
      var(--color-accent-primary) 100%
    );
    border-radius: var(--radius-full);
  }
`;

const ExperienceTimeline = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 50%;
    width: 2px;
    background: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0.05),
      var(--color-accent-primary),
      rgba(255, 255, 255, 0.05)
    );
    transform: translateX(-50%);
    
    @media (max-width: 768px) {
      left: 20px;
    }
  }
  
  @media (max-width: 768px) {
    padding-left: 40px;
  }
`;

const TimelineItem = styled(motion.div)`
  display: flex;
  margin-bottom: var(--spacing-lg);
  position: relative;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  &:nth-child(odd) {
    justify-content: flex-start;
    
    @media (max-width: 768px) {
      justify-content: flex-start;
    }
    
    .timeline-content {
      &::before {
        right: -15px;
        left: auto;
        border-width: 10px 0 10px 15px;
        border-color: transparent transparent transparent rgba(255, 255, 255, 0.02);
        
        @media (max-width: 768px) {
          right: auto;
          left: -15px;
          border-width: 10px 15px 10px 0;
          border-color: transparent rgba(255, 255, 255, 0.02) transparent transparent;
        }
      }
    }
  }
  
  &:nth-child(even) {
    justify-content: flex-end;
    
    @media (max-width: 768px) {
      justify-content: flex-start;
    }
    
    .timeline-marker {
      left: calc(50% - 15px);
      
      @media (max-width: 768px) {
        left: 13px;
      }
    }
    
    .timeline-content {
      &::before {
        left: -15px;
        border-width: 10px 15px 10px 0;
        border-color: transparent rgba(255, 255, 255, 0.02) transparent transparent;
      }
    }
  }
`;

const TimelineMarker = styled(motion.div)`
  position: absolute;
  left: calc(50% - 15px);
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: var(--color-bg-medium);
  border: 3px solid var(--color-accent-primary);
  z-index: 2;
  
  @media (max-width: 768px) {
    left: 13px;
  }
`;

const TimelineContent = styled(motion.div)`
  width: calc(50% - 40px);
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.05) 0%,
    rgba(255, 255, 255, 0.02) 100%
  );
  border-radius: var(--radius-lg);
  padding: var(--spacing-md);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 15px;
    width: 0;
    height: 0;
    border-style: solid;
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
  
  &.timeline-content {
    /* For the class-specific styling */
  }
`;

const TimelineDate = styled.div`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background-color: rgba(166, 140, 255, 0.1);
  color: var(--color-accent-primary);
  border-radius: var(--radius-full);
  margin-bottom: var(--spacing-sm);
  font-size: 0.875rem;
  font-weight: 500;
`;

const TimelineTitle = styled.h3`
  margin-bottom: 0.5rem;
  font-size: 1.5rem;
`;

const TimelineSubtitle = styled.h4`
  color: var(--color-accent-secondary);
  margin-bottom: var(--spacing-sm);
  font-size: 1rem;
  font-weight: 400;
`;

const TimelineDescription = styled.p`
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-sm);
  font-size: 1rem;
  line-height: 1.6;
`;

const Skills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: var(--spacing-sm);
`;

const Skill = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-full);
  font-size: 0.75rem;
  color: var(--color-text-secondary);
`;

const ExperienceSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.1 });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  // Experience data from resume
  const experienceData = [
    {
      id: 1,
      title: "School Psychologist",
      organization: "Erie Elementary",
      location: "St. Vrain Valley School District",
      period: "August 2024 - Present",
      description: "Currently supporting the school community with a focus on social emotional learning, executive functioning, and special education services.",
      skills: [
        "Autism Support",
        "Social Emotional Learning",
        "Executive Functioning",
        "Individual Counseling",
        "IEP Process",
        "Psychoeducational Assessment"
      ]
    },
    {
      id: 2,
      title: "School Psychologist",
      organization: "Eagleview Elementary",
      location: "Adams 12 School District",
      period: "March 2023 - April 2024",
      description: "Supported the Autism Center Based Program, providing comprehensive psychological services including direct instruction, counseling, and assessment.",
      skills: [
        "Threat & Risk Assessment",
        "Special Education Team Leadership",
        "IEP Development",
        "Consultation Services",
        "System Level Development",
        "MTSS Implementation"
      ]
    },
    {
      id: 3,
      title: "School Psychologist, Crisis Prevention Trainer, Gifted & Talented Liaison",
      organization: "Weld Re-8 School District",
      location: "Fort Lupton, CO",
      period: "August 2020 - March 2023",
      description: "Served multiple schools in the district while taking on specialized roles in crisis prevention and gifted education. Supervised and mentored school psychology interns.",
      skills: [
        "Crisis Response",
        "Mental Health Support",
        "Sources of Strength Implementation",
        "Special Education Compliance",
        "Transition Programming",
        "Functional Behavior Analysis"
      ]
    },
    {
      id: 4,
      title: "School Psychology Internship",
      organization: "Ft. Lupton High School & Homyak Pk-8",
      location: "Weld Re-8 School District",
      period: "August 2019 - May 2020",
      description: "Completed 1,200+ hours of supervised internship experience, applying school psychology practices in diverse educational settings.",
      skills: [
        "Assessment",
        "Counseling",
        "Consultation",
        "Intervention",
        "Crisis Response",
        "Data Analysis"
      ]
    },
    {
      id: 5,
      title: "Instructional Assistant/Case Manager",
      organization: "The Joshua School",
      location: "Denver, CO",
      period: "October 2014 - September 2016",
      description: "Created academic and behavioral program materials while providing one-on-one instruction for students with autism. Applied ABA strategies and collaborated with educational teams.",
      skills: [
        "Applied Behavior Analysis",
        "IEP Goal Development",
        "Data Collection",
        "Family Collaboration",
        "Staff Training",
        "Program Implementation"
      ]
    }
  ];

  return (
    <ExperienceContainer id="about">
      <GradientBackground />
      
      <ContentContainer>
        <SectionHeading>
          <HeadingLabel
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: false, amount: 0.2 }}
          >
            Professional Journey
          </HeadingLabel>
          
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: false, amount: 0.2 }}
          >
            My Experience
          </SectionTitle>
        </SectionHeading>
        
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <ExperienceTimeline>
            {experienceData.map((item, index) => (
              <TimelineItem key={item.id} variants={itemVariants}>
                <TimelineMarker 
                  initial={{ scale: 0, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: false, amount: 0.2 }}
                />
                
                <TimelineContent className="timeline-content">
                  <TimelineDate>{item.period}</TimelineDate>
                  <TimelineTitle>{item.title}</TimelineTitle>
                  <TimelineSubtitle>{item.organization}, {item.location}</TimelineSubtitle>
                  <TimelineDescription>{item.description}</TimelineDescription>
                  
                  <Skills>
                    {item.skills.map((skill, skillIndex) => (
                      <Skill key={skillIndex}>{skill}</Skill>
                    ))}
                  </Skills>
                </TimelineContent>
              </TimelineItem>
            ))}
          </ExperienceTimeline>
        </motion.div>
      </ContentContainer>
    </ExperienceContainer>
  );
};

export default ExperienceSection;